import React, { useLayoutEffect, useState } from 'react';
import { Box, Container } from '@mui/material';
import { Company } from '../types/site';
import Job from '../components/job/Job';

const companies: Company[] = [
  {
    name: 'M3 Computing, LLC',
    url: 'https://m3computing.com',
    dates: '2009 - Present',
    role: {
      title: 'Owner, Principal Engineer',
      description:
        'Founded and leads an education-focused technology consulting firm helping small and mid-sized businesses solve complex software development, cloud architecture, platform engineering, and modernization challenges.',
      functions: [
        'Serves as a hands-on technical advisor, architect, and builder, delivering Java, Python, Go, C#, web development, cloud-native, microservices, and secure platform solutions aligned with client business needs.',
        'Designs and delivers custom technical education programs in Spring, Java, Go, architecture, microservices, public cloud, and security to help engineering teams modernize practices and build sustainable internal capability.',
        'Combines consulting, code-level execution, and instructional design to help clients not only implement solutions but also understand, maintain, and evolve them as technology environments change.',
      ],
    },
  },
  {
    name: 'LinkedIn Learning',
    url: 'https://linkedin.com/learning',
    dates: '2017 - Present',
    role: {
      title: 'Content Author',
      description:
        'Creates and delivers LinkedIn Learning courses teaching software development, architecture, security, CI/CD, and modern engineering practices.',
      functions: [
        'Created and delivered 50+ LinkedIn Learning courses reaching more than 1.5M learners through hands-on coding demonstrations, structured learning paths, quizzes, and practical assessments.',
        'Develops course concepts, technical outlines, learning objectives, code-based instruction, and validation tools that help professional developers and software architects strengthen real-world skills and adapt to evolving software engineering environments.',
      ],
    },
  },
  {
    name: 'Vertex Inc.',
    url: 'https://vertexinc.com',
    dates: '2024 - 2026',
    role: {
      title: 'Principal Software Architect',
      description:
        'Served as a technology modernization leader, aligning cloud architecture, SaaS strategy, developer platforms, and engineering practices with business goals, compliance needs, and enterprise growth.',
      functions: [
        'Built and executed modernization roadmaps that advanced SaaS capabilities, strengthened developer experience, and accelerated adoption of scalable, cloud-native engineering practices.',
        'Designed multi-region, multi-cloud architecture to reduce platform risk, increase resiliency, and minimize exposure to single-provider limitations.',
        'Led cloud cost governance and utilization improvements, reducing spend by 30% while improving resource accountability and operational discipline.',
        'Championed adoption of Go, microservices and asynchronous messaging, and modern architecture patterns to improve software delivery, engineering productivity, and platform scalability.',
        'Developed and mentored 100+ engineers, architects, and interns through structured education, hands-on coaching, and a deep personal commitment to teaching and technical growth.',
        'Directed enterprise cloud migration strategy from AWS to Azure as the primary provider while preserving targeted OCI and AWS workloads to support business continuity, flexibility, and workload-specific performance needs.',
      ],
    },
  },
  {
    name: 'University of Kansas',
    url: 'https://ku.edu',
    dates: '2023 - 2024',
    role: {
      title: 'Adjunct Professor',
      description:
        'Developed and delivered graduate-level MSIT instruction in software architecture, database systems, cyber law, and privacy.',
      functions: [
        'Created course content, selected instructional materials, designed projects, and built test-driven assessments to strengthen applied technical learning.',
        'Fostered an active learning environment rooted in hands-on practice, real-world problem solving, and student engagement.',
        'Designed project-based learning experiences that challenged students to analyze complex technology, architecture, security, and privacy issues while developing critical thinking and execution skills needed for professional IT environments.',
      ],
    },
  },
  {
    name: 'Catch+Release',
    url: 'https://catchandrelease.com',
    dates: '2021 - 2023',
    role: {
      title: 'Senior Principal Engineer',
      description:
        'Architected and developed a cloud-native SaaS platform on AWS, integrating payment processing and licensing workflows to support creator compensation, digital rights monetization, and scalable platform growth.',
      functions: [
        'Designed autonomous rights acquisition and licensing workflows using Temporal, enabling reliable orchestration of complex, multi-step content licensing processes.',
        'Built AI-enabled asset ingestion, tagging, search, and web content discovery capabilities to improve metadata intelligence, licensing efficiency, and digital content monetization.',
      ],
    },
  },
  {
    name: 'DataStax Inc',
    url: 'https://datastax.com',
    dates: '2018 - 2021',
    role: {
      title: 'Cloud Platform and Application Architect',
      description:
        'Architected, designed, and led full lifecycle development of DataStax Astra DBaaS on Apache Cassandra, helping create one of the first vendor-agnostic serverless database platforms.',
      functions: [
        'Built the cloud platform, automation, and application architecture that enabled customers to deploy and access Apache Cassandra database services through a simplified, click-to-provision experience across multi-cloud and multi-region environments.',
        'Defined the technical vision and roadmap for multi-cloud, serverless, and private data center platform capabilities, leveraging Kubernetes, microservices, infrastructure as code, CI/CD, configuration management, secrets management, logging, metrics, and alerting.',
        'Led engineers and engineering managers through product design, development, release, operations, and incident management, establishing SDLC and operational processes that supported 99.99% customer uptime.',
        'Integrated payment, IAM, CRM, developer platform, and operational systems to support global scale, repeatable cloud deployment patterns, and ongoing commercialization of DataStax\'s cloud database platform.',
      ],
    },
  },
  {
    name: 'Garmin International',
    url: 'https://garmin.com',
    dates: '2010 - 2018',
    role: {
      title: 'Internet Architect',
      description:
        'Designed and advanced a unified enterprise development platform, leveraging Cloud Foundry and Kubernetes to modernize application delivery and standardize scalable deployment practices.',
      functions: [
        'Developed a multi-data center private cloud offering, including support for an air-gapped China data center, strengthening global platform resilience, security, and operational control.',
        'Modernized SDLC practices and developer toolkits to improve engineering consistency, accelerate delivery, and support adoption of more efficient software development standards.',
        'Designed web developer security and privacy platforms that supported GDPR and PCI compliance, embedding stronger governance, data protection, and regulatory alignment into application development practices.',
      ],
    },
  },
  {
    name: 'Cerner Corporation',
    url: 'https://cerner.com',
    dates: 'Oct 2002 - Oct 2010',
    role: {
      title: 'Software Engineer',
      description:
        'Progressed through multiple roles across PharmNet and Discern ISIS, spanning functional testing, technical support, software development, and architecture.',
      functions: [
        'Wrote entire backend of Discern ISIS using Java, Spring, and Apache CXF SOAP based services as a green field solution.',
        'Managed infrastructure for Java Application Deployment.',
        'Designed and built a Hibernate based abstraction framework with parental relationships for DB2 and Oracle RDBMS.',
        'Implemented first web service based wrapper for Cerner Multum.',
        'Designed and wrote software using Visual Basic, C, C++, Java, and CCL.',
        'Managed and enhanced the Dose Range Checker.',
        'Rewrote Pharmacy Sharable order right server from C to Java.',
      ],
    },
  },
];

const Experience = (): JSX.Element => {
  const [results, setResults] = useState<Company[]>([]);

  useLayoutEffect(() => {
    setResults(companies);
  }, []);

  return (
    <Container>
      <Box>
        {results.map((company, i) => {
          return <Job name={company.name} url={company.url} dates={company.dates} role={company.role} key={i} />;
        })}
      </Box>
    </Container>
  );
};

export default Experience;
